
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_6: AIModule = {
  id: 'game-ai-optimization-performance',
  title: 'Game AI Optimization & Performance',
  description: 'Learn techniques for optimizing AI performance in real-time games, including profiling, multithreading, and scalability.',
  duration: '3 hours',
  difficulty: 'hard',
  content: `
# Game AI Optimization & Performance

Optimizing AI performance is crucial for maintaining smooth gameplay, especially in real-time games with complex AI systems and multiple agents.

## Performance Profiling and Analysis

### Key Performance Metrics

1. **Frame Time Impact**: AI processing time per frame
2. **Memory Usage**: AI data structures and caching
3. **CPU Utilization**: Distribution across cores
4. **Scalability**: Performance with increasing agent count

### Profiling Tools and Techniques

\`\`\`python
import time
import threading
import multiprocessing
from typing import List, Dict, Callable, Any
from dataclasses import dataclass
from collections import deque
import numpy as np
from concurrent.futures import ThreadPoolExecutor, ProcessPoolExecutor
import cProfile
import pstats
from functools import wraps

@dataclass
class PerformanceMetrics:
    function_name: str
    execution_time: float
    memory_usage: int
    call_count: int
    cpu_usage: float

class AIProfiler:
    """Profiler for AI system performance analysis"""
    
    def __init__(self):
        self.metrics: Dict[str, List[PerformanceMetrics]] = {}
        self.frame_times: deque = deque(maxlen=1000)
        self.memory_snapshots: deque = deque(maxlen=100)
        
    def profile_function(self, func_name: str = None):
        """Decorator for profiling function performance"""
        def decorator(func):
            @wraps(func)
            def wrapper(*args, **kwargs):
                start_time = time.perf_counter()
                start_memory = self._get_memory_usage()
                
                result = func(*args, **kwargs)
                
                end_time = time.perf_counter()
                end_memory = self._get_memory_usage()
                
                execution_time = end_time - start_time
                memory_delta = end_memory - start_memory
                
                name = func_name or func.__name__
                metric = PerformanceMetrics(
                    function_name=name,
                    execution_time=execution_time,
                    memory_usage=memory_delta,
                    call_count=1,
                    cpu_usage=0.0  # Would need external tool for accurate CPU usage
                )
                
                if name not in self.metrics:
                    self.metrics[name] = []
                self.metrics[name].append(metric)
                
                return result
            return wrapper
        return decorator
    
    def _get_memory_usage(self) -> int:
        """Get current memory usage (simplified)"""
        import psutil
        process = psutil.Process()
        return process.memory_info().rss
    
    def start_frame_timing(self):
        """Start timing for current frame"""
        self.frame_start_time = time.perf_counter()
    
    def end_frame_timing(self):
        """End timing for current frame"""
        if hasattr(self, 'frame_start_time'):
            frame_time = time.perf_counter() - self.frame_start_time
            self.frame_times.append(frame_time)
    
    def get_performance_report(self) -> Dict[str, Any]:
        """Generate comprehensive performance report"""
        report = {
            'function_metrics': {},
            'frame_timing': {},
            'recommendations': []
        }
        
        # Analyze function metrics
        for func_name, metrics_list in self.metrics.items():
            total_time = sum(m.execution_time for m in metrics_list)
            avg_time = total_time / len(metrics_list)
            max_time = max(m.execution_time for m in metrics_list)
            call_count = len(metrics_list)
            
            report['function_metrics'][func_name] = {
                'total_time': total_time,
                'average_time': avg_time,
                'max_time': max_time,
                'call_count': call_count,
                'time_per_call': avg_time,
                'total_memory_delta': sum(m.memory_usage for m in metrics_list)
            }
            
            # Generate recommendations
            if avg_time > 0.016:  # > 16ms (one frame at 60fps)
                report['recommendations'].append(
                    f"Function '{func_name}' takes {avg_time*1000:.2f}ms on average - consider optimization"
                )
        
        # Analyze frame timing
        if self.frame_times:
            frame_array = np.array(self.frame_times)
            report['frame_timing'] = {
                'average_frame_time': np.mean(frame_array),
                'max_frame_time': np.max(frame_array),
                'min_frame_time': np.min(frame_array),
                'frame_time_std': np.std(frame_array),
                'fps_estimate': 1.0 / np.mean(frame_array),
                'frame_drops': np.sum(frame_array > 0.016)  # Frames > 16ms
            }
        
        return report

class OptimizedAIManager:
    """High-performance AI manager with various optimization techniques"""
    
    def __init__(self, max_threads: int = 4):
        self.agents: List['OptimizedAgent'] = []
        self.thread_pool = ThreadPoolExecutor(max_workers=max_threads)
        self.profiler = AIProfiler()
        
        # Level-of-detail settings
        self.lod_distances = {
            'high': 50.0,    # Full AI processing
            'medium': 150.0, # Reduced AI frequency
            'low': 300.0,    # Basic AI only
            'culled': 500.0  # No AI processing
        }
        
        # Performance budgets (milliseconds per frame)
        self.ai_budget_per_frame = 8.0  # 8ms budget for AI
        self.current_frame_time = 0.0
    
    def add_agent(self, agent: 'OptimizedAgent'):
        """Add an AI agent to the manager"""
        self.agents.append(agent)
    
    @AIProfiler().profile_function("update_all_agents")
    def update_all_agents(self, player_position: tuple, delta_time: float):
        """Update all agents with performance optimizations"""
        self.profiler.start_frame_timing()
        
        # Calculate distances and assign LOD levels
        self._assign_lod_levels(player_position)
        
        # Group agents by LOD level for batch processing
        lod_groups = self._group_agents_by_lod()
        
        # Process high-priority agents first
        high_priority_agents = lod_groups.get('high', [])
        if high_priority_agents:
            self._update_agents_batch(high_priority_agents, delta_time, full_update=True)
        
        # Process medium priority if budget allows
        remaining_budget = self.ai_budget_per_frame - self.current_frame_time
        if remaining_budget > 2.0:  # At least 2ms remaining
            medium_priority_agents = lod_groups.get('medium', [])
            if medium_priority_agents:
                self._update_agents_batch(medium_priority_agents, delta_time, full_update=False)
        
        # Process low priority on alternating frames
        if int(time.time() * 60) % 2 == 0:  # Every other frame at 60fps
            low_priority_agents = lod_groups.get('low', [])
            if low_priority_agents:
                self._update_agents_basic(low_priority_agents, delta_time)
        
        self.profiler.end_frame_timing()
    
    def _assign_lod_levels(self, player_position: tuple):
        """Assign level-of-detail based on distance to player"""
        for agent in self.agents:
            distance = self._calculate_distance(agent.position, player_position)
            
            if distance <= self.lod_distances['high']:
                agent.lod_level = 'high'
            elif distance <= self.lod_distances['medium']:
                agent.lod_level = 'medium'
            elif distance <= self.lod_distances['low']:
                agent.lod_level = 'low'
            else:
                agent.lod_level = 'culled'
    
    def _group_agents_by_lod(self) -> Dict[str, List['OptimizedAgent']]:
        """Group agents by their LOD level"""
        groups = {'high': [], 'medium': [], 'low': [], 'culled': []}
        for agent in self.agents:
            groups[agent.lod_level].append(agent)
        return groups
    
    @AIProfiler().profile_function("update_agents_batch")
    def _update_agents_batch(self, agents: List['OptimizedAgent'], 
                           delta_time: float, full_update: bool = True):
        """Update a batch of agents, optionally using multithreading"""
        start_time = time.perf_counter()
        
        if len(agents) > 10 and full_update:
            # Use multithreading for large batches
            batch_size = len(agents) // 4  # Split into 4 batches
            batches = [agents[i:i+batch_size] for i in range(0, len(agents), batch_size)]
            
            futures = []
            for batch in batches:
                future = self.thread_pool.submit(self._update_agent_batch_threaded, batch, delta_time)
                futures.append(future)
            
            # Wait for all batches to complete
            for future in futures:
                future.result()
        else:
            # Single-threaded update
            for agent in agents:
                if full_update:
                    agent.full_update(delta_time)
                else:
                    agent.reduced_update(delta_time)
        
        self.current_frame_time += (time.perf_counter() - start_time) * 1000
    
    def _update_agent_batch_threaded(self, agents: List['OptimizedAgent'], delta_time: float):
        """Thread worker function for updating agent batches"""
        for agent in agents:
            agent.full_update(delta_time)
    
    @AIProfiler().profile_function("update_agents_basic")
    def _update_agents_basic(self, agents: List['OptimizedAgent'], delta_time: float):
        """Basic update for low-priority agents"""
        for agent in agents:
            agent.basic_update(delta_time)
    
    def _calculate_distance(self, pos1: tuple, pos2: tuple) -> float:
        """Fast distance calculation"""
        dx = pos1[0] - pos2[0]
        dy = pos1[1] - pos2[1]
        return (dx * dx + dy * dy) ** 0.5  # Avoid sqrt for distance comparison when possible
    
    def get_performance_stats(self) -> Dict:
        """Get current performance statistics"""
        return self.profiler.get_performance_report()

class OptimizedAgent:
    """AI agent optimized for performance"""
    
    def __init__(self, agent_id: str, position: tuple):
        self.agent_id = agent_id
        self.position = position
        self.lod_level = 'high'
        
        # Cached computations
        self._cached_pathfinding_result = None
        self._pathfinding_cache_time = 0
        self._cache_timeout = 1.0  # 1 second cache timeout
        
        # State for reduced updates
        self.last_full_update = 0
        self.update_frequency = {
            'high': 0.016,    # 60fps
            'medium': 0.033,  # 30fps
            'low': 0.1        # 10fps
        }
        
        # Pre-allocated data structures
        self.nearby_agents = []
        self.vision_targets = []
        
    def full_update(self, delta_time: float):
        """Complete AI update with all systems"""
        current_time = time.time()
        
        # Pathfinding with caching
        if (current_time - self._pathfinding_cache_time) > self._cache_timeout:
            self._cached_pathfinding_result = self._calculate_pathfinding()
            self._pathfinding_cache_time = current_time
        
        # Vision and perception
        self._update_vision()
        
        # Decision making
        self._update_decision_tree()
        
        # Movement and actions
        self._update_movement(delta_time)
        
        self.last_full_update = current_time
    
    def reduced_update(self, delta_time: float):
        """Reduced AI update for medium priority"""
        current_time = time.time()
        
        # Skip some expensive operations
        if (current_time - self.last_full_update) > self.update_frequency['medium']:
            # Only update essential systems
            self._update_movement(delta_time)
            self._basic_decision_making()
            self.last_full_update = current_time
    
    def basic_update(self, delta_time: float):
        """Minimal AI update for low priority"""
        # Only essential movement and state updates
        self._update_movement(delta_time)
    
    def _calculate_pathfinding(self) -> List[tuple]:
        """Pathfinding calculation with result caching"""
        # Simulated pathfinding - would use A* or other algorithm
        # This is a placeholder for demonstration
        return [(self.position[0] + 1, self.position[1])]
    
    def _update_vision(self):
        """Update agent vision and perception"""
        # Simulated vision system
        # Use spatial partitioning for efficient queries
        pass
    
    def _update_decision_tree(self):
        """Full decision tree processing"""
        # Simulated decision making
        pass
    
    def _basic_decision_making(self):
        """Simplified decision making for reduced updates"""
        # Simulated basic decisions
        pass
    
    def _update_movement(self, delta_time: float):
        """Update agent movement"""
        if self._cached_pathfinding_result:
            # Move towards next waypoint
            target = self._cached_pathfinding_result[0]
            dx = target[0] - self.position[0]
            dy = target[1] - self.position[1]
            
            speed = 50.0 * delta_time
            if abs(dx) > speed:
                dx = speed if dx > 0 else -speed
            if abs(dy) > speed:
                dy = speed if dy > 0 else -speed
            
            self.position = (self.position[0] + dx, self.position[1] + dy)

class SpatialPartitioning:
    """Spatial partitioning system for efficient agent queries"""
    
    def __init__(self, world_size: tuple, cell_size: float):
        self.world_size = world_size
        self.cell_size = cell_size
        self.grid_width = int(world_size[0] / cell_size) + 1
        self.grid_height = int(world_size[1] / cell_size) + 1
        self.grid: Dict[tuple, List['OptimizedAgent']] = {}
    
    def clear(self):
        """Clear all agents from grid"""
        self.grid.clear()
    
    def add_agent(self, agent: 'OptimizedAgent'):
        """Add agent to spatial grid"""
        cell = self._get_cell(agent.position)
        if cell not in self.grid:
            self.grid[cell] = []
        self.grid[cell].append(agent)
    
    def get_nearby_agents(self, position: tuple, radius: float) -> List['OptimizedAgent']:
        """Get agents within radius of position"""
        cells_to_check = self._get_cells_in_radius(position, radius)
        nearby_agents = []
        
        for cell in cells_to_check:
            if cell in self.grid:
                for agent in self.grid[cell]:
                    distance_sq = ((agent.position[0] - position[0]) ** 2 + 
                                 (agent.position[1] - position[1]) ** 2)
                    if distance_sq <= radius * radius:
                        nearby_agents.append(agent)
        
        return nearby_agents
    
    def _get_cell(self, position: tuple) -> tuple:
        """Get grid cell for position"""
        x = int(position[0] / self.cell_size)
        y = int(position[1] / self.cell_size)
        return (max(0, min(x, self.grid_width - 1)), 
                max(0, min(y, self.grid_height - 1)))
    
    def _get_cells_in_radius(self, position: tuple, radius: float) -> List[tuple]:
        """Get all cells that intersect with radius"""
        cells = []
        cell_radius = int(radius / self.cell_size) + 1
        center_cell = self._get_cell(position)
        
        for dx in range(-cell_radius, cell_radius + 1):
            for dy in range(-cell_radius, cell_radius + 1):
                cell_x = center_cell[0] + dx
                cell_y = center_cell[1] + dy
                
                if (0 <= cell_x < self.grid_width and 
                    0 <= cell_y < self.grid_height):
                    cells.append((cell_x, cell_y))
        
        return cells

# Memory pool for frequent allocations
class ObjectPool:
    """Object pool to reduce memory allocations"""
    
    def __init__(self, object_type: type, initial_size: int = 100):
        self.object_type = object_type
        self.available_objects = deque()
        self.in_use_objects = set()
        
        # Pre-allocate objects
        for _ in range(initial_size):
            obj = object_type()
            self.available_objects.append(obj)
    
    def get_object(self):
        """Get an object from the pool"""
        if self.available_objects:
            obj = self.available_objects.popleft()
        else:
            obj = self.object_type()
        
        self.in_use_objects.add(obj)
        return obj
    
    def return_object(self, obj):
        """Return an object to the pool"""
        if obj in self.in_use_objects:
            self.in_use_objects.remove(obj)
            # Reset object state if needed
            if hasattr(obj, 'reset'):
                obj.reset()
            self.available_objects.append(obj)

# Performance testing and benchmarking
def benchmark_ai_performance():
    """Benchmark AI system performance"""
    
    print("=== AI Performance Benchmark ===")
    
    # Create test scenario
    ai_manager = OptimizedAIManager(max_threads=4)
    spatial_grid = SpatialPartitioning((1000, 1000), 50.0)
    
    # Create test agents
    num_agents = 500
    agents = []
    for i in range(num_agents):
        position = (np.random.uniform(0, 1000), np.random.uniform(0, 1000))
        agent = OptimizedAgent(f"agent_{i}", position)
        agents.append(agent)
        ai_manager.add_agent(agent)
    
    print(f"Created {num_agents} agents for testing")
    
    # Benchmark without optimizations
    print("\\nBenchmarking without optimizations...")
    start_time = time.perf_counter()
    
    for frame in range(100):  # 100 frames
        for agent in agents:
            agent.full_update(0.016)  # 60fps delta time
    
    unoptimized_time = time.perf_counter() - start_time
    print(f"Unoptimized: {unoptimized_time:.3f}s for 100 frames")
    
    # Benchmark with optimizations
    print("\\nBenchmarking with optimizations...")
    start_time = time.perf_counter()
    
    player_position = (500, 500)  # Center of world
    
    for frame in range(100):  # 100 frames
        ai_manager.current_frame_time = 0.0
        ai_manager.update_all_agents(player_position, 0.016)
    
    optimized_time = time.perf_counter() - start_time
    print(f"Optimized: {optimized_time:.3f}s for 100 frames")
    
    # Calculate improvement
    improvement = (unoptimized_time - optimized_time) / unoptimized_time * 100
    print(f"Performance improvement: {improvement:.1f}%")
    
    # Get detailed performance report
    performance_report = ai_manager.get_performance_stats()
    print("\\nPerformance Report:")
    
    if 'frame_timing' in performance_report:
        frame_timing = performance_report['frame_timing']
        print(f"  Average frame time: {frame_timing.get('average_frame_time', 0)*1000:.2f}ms")
        print(f"  Estimated FPS: {frame_timing.get('fps_estimate', 0):.1f}")
        print(f"  Frame drops: {frame_timing.get('frame_drops', 0)}")
    
    print("\\nFunction Performance:")
    for func_name, metrics in performance_report.get('function_metrics', {}).items():
        print(f"  {func_name}:")
        print(f"    Average time: {metrics['average_time']*1000:.2f}ms")
        print(f"    Call count: {metrics['call_count']}")
    
    # Recommendations
    recommendations = performance_report.get('recommendations', [])
    if recommendations:
        print("\\nRecommendations:")
        for rec in recommendations:
            print(f"  - {rec}")

# Run benchmark
benchmark_ai_performance()
\`\`\`

## Optimization Strategies

### 1. Level of Detail (LOD)
- Reduce AI complexity based on distance to player
- Use different update frequencies for different priorities
- Cull distant agents entirely

### 2. Multithreading and Parallelism
- Parallel processing of independent agents
- Thread-safe data structures
- Load balancing across cores

### 3. Caching and Memoization
- Cache expensive computations
- Reuse pathfinding results
- Store frequently accessed data

### 4. Spatial Partitioning
- Grid-based spatial queries
- Octrees for 3D environments
- Efficient neighbor finding

### 5. Memory Management
- Object pooling for frequent allocations
- Minimize garbage collection
- Pre-allocate data structures

## Scalability Considerations

### Agent Limiting
- Maximum agent counts per area
- Dynamic spawning/despawning
- Priority-based processing

### Adaptive Quality
- Automatic quality adjustment based on performance
- Player-configurable AI density
- Platform-specific optimizations

### Performance Budgets
- Time budgets per frame
- Memory usage limits
- CPU usage monitoring

Proper AI optimization ensures smooth gameplay while maintaining intelligent and responsive AI behavior, even with large numbers of agents in complex game worlds.
  `,
  quiz: {
    id: 'ai-optimization-quiz',
    title: 'Game AI Optimization Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the main purpose of Level of Detail (LOD) in AI systems?',
        type: 'multiple-choice',
        options: [
          'To improve graphics quality',
          'To reduce AI processing load based on importance/distance',
          'To increase AI intelligence',
          'To simplify AI programming'
        ],
        correctAnswer: 1,
        explanation: 'LOD reduces computational load by processing distant or less important agents with simpler AI logic, maintaining performance while keeping nearby agents fully intelligent.',
        difficulty: 'hard',
        points: 20
      },
      {
        id: 'q2',
        question: 'What is the benefit of spatial partitioning in AI systems?',
        type: 'multiple-choice',
        options: [
          'It makes AI smarter',
          'It enables efficient neighbor queries and collision detection',
          'It reduces memory usage',
          'It improves graphics rendering'
        ],
        correctAnswer: 1,
        explanation: 'Spatial partitioning divides the world into grid cells, allowing for fast queries of nearby objects without checking every agent against every other agent.',
        difficulty: 'hard',
        points: 15
      }
    ]
  }
};
